import { createRouter, createWebHistory } from 'vue-router'
import { loginClient } from '@/lib/auth'
import AppLayout from '@/layouts/AppLayout.vue'
import { useAccountStore } from '@/stores/account'

export const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_PATH ?? '/'),
  routes: [
    // Unauthenticated routes
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
    },
    {
      path: '/onboarding',
      name: 'onboarding',
      component: () => import('@/views/OnboardingView.vue'),
      meta: { requiresAuth: true },
    },
    // Authenticated routes — all rendered inside AppLayout (sidebar)
    {
      path: '/',
      component: AppLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'inbox',
          component: () => import('@/views/InboxView.vue'),
        },
        {
          path: 'threads/:id',
          name: 'thread-detail',
          component: () => import('@/views/ThreadDetailView.vue'),
        },
        {
          path: 'quarantine',
          name: 'quarantine',
          component: () => import('@/views/QuarantineView.vue'),
        },
        {
          path: 'quarantine/:id',
          name: 'quarantine-detail',
          component: () => import('@/views/QuarantineDetailView.vue'),
        },
        {
          path: 'spam',
          name: 'spam',
          component: () => import('@/views/SpamView.vue'),
        },
        {
          path: 'spam/:id',
          name: 'spam-detail',
          component: () => import('@/views/SpamDetailView.vue'),
        },
        {
          path: 'drafts',
          name: 'drafts',
          component: () => import('@/views/DraftsView.vue'),
        },
        {
          path: 'search',
          name: 'search',
          component: () => import('@/views/SearchView.vue'),
        },
        {
          path: 'templates',
          name: 'templates',
          component: () => import('@/views/TemplatesView.vue'),
        },
        {
          path: 'labels',
          name: 'labels',
          component: () => import('@/views/LabelsView.vue'),
        },
        {
          path: 'resources',
          name: 'resources',
          component: () => import('@/views/ResourcesView.vue'),
        },
        {
          path: 'rules',
          name: 'rules',
          component: () => import('@/views/RulesView.vue'),
        },
        {
          path: 'rules/new',
          name: 'rules-new',
          component: () => import('@/views/RuleEditorView.vue'),
        },
        {
          path: 'rules/:id',
          name: 'rules-edit',
          component: () => import('@/views/RuleEditorView.vue'),
        },
        {
          path: 'settings',
          redirect: '/settings/email-forwarding',
        },
        {
          path: 'settings/:tab',
          name: 'settings',
          component: () => import('@/views/SettingsView.vue'),
        },
        {
          path: 'billing',
          name: 'billing',
          component: () => import('@/views/BillingView.vue'),
        },
        {
          path: 'audit-log',
          name: 'audit-log',
          component: () => import('@/views/AuditLogView.vue'),
        },
        {
          path: 'changelog',
          name: 'changelog',
          component: () => import('@/views/ChangelogView.vue'),
        },
        {
          path: 'stats',
          name: 'stats',
          component: () => import('@/views/StatsView.vue'),
        },
        {
          path: 'support',
          name: 'support',
          component: () => import('@/views/SupportView.vue'),
        },
        {
          path: 'admin',
          name: 'admin',
          component: () => import('@/views/AdminView.vue'),
        },
      ],
    },

    // Catch-all — redirect unknown paths to inbox
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },

    // Legal pages (no sidebar)
    {
      path: '/terms',
      name: 'terms',
      component: () => import('@/views/LegalView.vue'),
      props: { page: 'terms' },
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('@/views/LegalView.vue'),
      props: { page: 'privacy' },
    },

    // Invite accept — unauthenticated so the nav guard doesn't redirect before login
    {
      path: '/registration',
      name: 'registration',
      component: () => import('@/views/InviteView.vue'),
    },
  ],
})

const ROUTE_TITLES: Record<string, string> = {
  inbox: 'Inbox',
  'thread-detail': 'Conversation',
  quarantine: 'Quarantine',
  'quarantine-detail': 'Quarantined email',
  spam: 'Spam',
  'spam-detail': 'Blocked email',
  drafts: 'Drafts',
  search: 'Search',
  labels: 'Labels & Views',
  resources: 'Resources',
  rules: 'Rules',
  'rules-new': 'New rule',
  'rules-edit': 'Edit rule',
  templates: 'Templates',
  settings: 'Settings',
  billing: 'Billing',
  'audit-log': 'Audit log',
  changelog: 'Changelog',
  support: 'Support',
  admin: 'Admin',
  login: 'Sign in',
  onboarding: 'Setup',
  terms: 'Terms of service',
  privacy: 'Privacy policy',
  registration: 'Accept invitation',
}

const APP_NAME = 'SES Email Adapter'

router.beforeEach(async (to) => {
  // Cross-cutting: detect ?accountId= and set account context before any other guard logic
  const queryAccountId = to.query.accountId as string | undefined
  if (queryAccountId) {
    const accountStore = useAccountStore()
    accountStore.startFetch(queryAccountId)
    const { accountId: _, ...rest } = to.query
    return { ...to, query: rest, replace: true }
  }

  if (!to.meta.requiresAuth) return true

  const authenticated = await loginClient.userSessionExists()
  if (!authenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // Onboarding manages its own account creation — let it through always
  if (to.name === 'onboarding') return true

  // Optimistic hydration: if the accounts store hydrated a cached account that has already
  // completed onboarding, let the shell render immediately and let the background fetch
  // (kicked off in main.ts) reconcile. Only block on the network when there's nothing
  // trustworthy to render yet — no cached account, or one still mid-onboarding — since that
  // decision drives the onboarding redirect below and must not be made against stale state.
  const accountStore = useAccountStore()
  const hasOnboardedAccount = accountStore.account?.onboarding?.completed === true
  if (!hasOnboardedAccount) {
    await accountStore.waitForFetch()
  }

  // Redirect to onboarding if no account exists or onboarding hasn't been completed.
  // Profile and billing are exempt — users can navigate there any time they're authenticated.
  const exemptFromOnboardingRedirect = new Set(['billing'])
  if (
    !exemptFromOnboardingRedirect.has(String(to.name)) &&
    (!accountStore.accountId || !accountStore.account?.onboarding?.completed)
  ) {
    return { name: 'onboarding' }
  }
  return true
})

router.afterEach((to) => {
  const routeTitle = to.name ? ROUTE_TITLES[String(to.name)] : undefined
  document.title = routeTitle ? `${routeTitle} — ${APP_NAME}` : APP_NAME
})
